import { usePrincipalKey } from "@auth/sessionStore";
import {
  Button,
  EmptyNote,
  Input,
  PanelError,
  SegmentedControl,
  Select,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { CatalogPanel } from "./CatalogPanel";
import { JevCallsPanel } from "./JevCallsPanel";
import { TraceDetail } from "./TraceDetail";
import { duration, clinicTime, words } from "./traceFormat";
import { tracePageOptions, useTracePage } from "./traceQueries";
import type { TraceSummary } from "./types";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "running", label: "Running" },
  { value: "waiting", label: "Waiting" },
  { value: "done", label: "Complete" },
  { value: "exhausted", label: "Exhausted" },
  { value: "parked", label: "Parked" },
];

const outcome = (trace: TraceSummary): string => {
  const status =
    trace.status === "done"
      ? "Processing complete"
      : trace.status === "exhausted"
        ? "Execution exhausted"
        : trace.status === "parked"
          ? "Execution parked"
          : words(trace.status);
  const facts: string[] = [];
  if (trace.decision_outcome === "no_op") facts.push("Decision: no action");
  if (trace.action_counts.exhausted > 0)
    facts.push(`${trace.action_counts.exhausted} exhausted`);
  if (trace.action_counts.parked > 0)
    facts.push(`${trace.action_counts.parked} parked`);
  if (trace.action_counts.stale > 0)
    facts.push(`${trace.action_counts.stale} stale`);
  if (trace.action_counts.no_op > 0) {
    const count = trace.action_counts.no_op;
    facts.push(`${count} action${count === 1 ? "" : "s"} made no change`);
  }
  return [...facts, status].join(" · ");
};

const TraceRow = ({
  trace,
  timezone,
  expanded,
  active,
  onToggle,
  onOpenEvent,
}: {
  trace: TraceSummary;
  timezone: string;
  expanded: boolean;
  active: boolean;
  onToggle: () => void;
  onOpenEvent: (uuid: string) => void;
}) => (
  <li className="border-b border-rule last:border-b-0" data-dd-privacy="hidden">
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left hover:bg-smudge focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent sm:grid-cols-[auto_minmax(8rem,1fr)_minmax(10rem,2fr)_minmax(7rem,1fr)_auto] sm:gap-4"
    >
      <span aria-hidden="true" className="font-mono text-sm text-annotation">
        {expanded ? "⌄" : "›"}
      </span>
      <span className="min-w-0">
        <time
          dateTime={trace.occurred_at}
          className="block text-mono-time text-xs text-ink-body"
        >
          {clinicTime(trace.occurred_at, timezone)}
        </time>
        <span className="mt-1 block truncate text-xs text-annotation sm:hidden">
          {words(trace.kind)}
        </span>
      </span>
      <span className="hidden min-w-0 sm:block">
        <span className="block truncate text-sm font-medium text-ink">
          {trace.source?.text || words(trace.kind)}
        </span>
        <span className="mt-1 block font-mono text-xs text-annotation">
          {words(trace.kind)}
          {trace.source?.current ? " · current linked source" : ""}
        </span>
      </span>
      <span className="hidden min-w-0 sm:block">
        <span className="block text-xs text-ink-body">{outcome(trace)}</span>
        <span className="mt-1 block truncate text-xs text-annotation">
          {trace.source?.room_name || "Room unknown"}
        </span>
      </span>
      <span className="text-right font-mono text-xs text-annotation">
        {duration(trace.elapsed_ms)}
      </span>
      <span className="col-span-2 col-start-2 block text-xs text-ink-body sm:hidden">
        {outcome(trace)} · {trace.source?.room_name || "Room unknown"}
      </span>
      <span className="col-span-2 col-start-2 block truncate text-sm font-medium text-ink sm:hidden">
        {trace.source?.text || words(trace.kind)}
      </span>
    </button>
    {expanded && (
      <TraceDetail
        eventUuid={trace.event_uuid}
        timezone={timezone}
        active={active}
        onOpenEvent={onOpenEvent}
      />
    )}
  </li>
);

type TracesView = "activity" | "catalog" | "jev";

const VIEW_OPTIONS = [
  { value: "activity" as const, label: "Activity" },
  { value: "catalog" as const, label: "Catalog" },
  { value: "jev" as const, label: "Jev calls" },
];

export const TracesPanel = ({ active }: { active: boolean }) => {
  const principal = usePrincipalKey();
  return <PrincipalTracesPanel key={principal} active={active} />;
};

const PrincipalTracesPanel = ({ active }: { active: boolean }) => {
  const [view, setView] = useState<TracesView>("activity");
  // A view mounts (and fetches) on first open, then stays mounted with its state.
  const [opened, setOpened] = useState(() => new Set<TracesView>([view]));
  if (!opened.has(view)) setOpened(new Set(opened).add(view));
  return (
    <div className="min-w-0 space-y-4">
      <SegmentedControl
        aria-label="Traces view"
        options={VIEW_OPTIONS}
        value={view}
        onValueChange={setView}
        size="sm"
      />
      <div hidden={view !== "activity"}>
        <ActivityPanel active={active && view === "activity"} />
      </div>
      {opened.has("catalog") && (
        <section
          hidden={view !== "catalog"}
          className="min-w-0 font-body text-ink-body"
          data-dd-privacy="hidden"
        >
          <CatalogPanel active={active && view === "catalog"} />
        </section>
      )}
      {opened.has("jev") && (
        <div hidden={view !== "jev"}>
          <JevCallsPanel active={active && view === "jev"} />
        </div>
      )}
    </div>
  );
};

const ActivityPanel = ({ active }: { active: boolean }) => {
  const principal = usePrincipalKey();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<TraceSummary["status"] | "">("");
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loadingNext, setLoadingNext] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [nextError, setNextError] = useState(false);
  const [previousError, setPreviousError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigationRequest = useRef(0);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [relatedEvent, setRelatedEvent] = useState<string | null>(null);
  const resetPage = useCallback(() => {
    navigationRequest.current += 1;
    setPageCursors([undefined]);
    setPageIndex(0);
    setLoadingNext(false);
    setLoadingPrevious(false);
    setNextError(false);
    setPreviousError(false);
    setExpanded(new Set());
    setRelatedEvent(null);
  }, []);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const nextSearch = searchText.trim();
      if (nextSearch !== search) {
        resetPage();
        setSearch(nextSearch);
      }
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchText, search, resetPage]);
  const query = useTracePage(active, search, status, pageCursors[pageIndex]);
  const rows = query.data?.items ?? [];
  const timezone = query.data?.clinic_timezone || "UTC";
  const seen = new Set(rows.map((trace) => trace.event_uuid));
  const toggle = (trace: TraceSummary) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(trace.event_uuid)) next.delete(trace.event_uuid);
      else next.add(trace.event_uuid);
      return next;
    });
  };
  const openRelated = (eventUuid: string) => {
    if (seen.has(eventUuid)) {
      setExpanded((current) => new Set(current).add(eventUuid));
      setRelatedEvent(null);
    } else {
      setRelatedEvent(eventUuid);
    }
  };
  const nextPage = async () => {
    const cursor = query.data?.next_cursor;
    if (
      !active ||
      !cursor ||
      loadingNext ||
      loadingPrevious ||
      query.isFetching
    )
      return;
    const request = ++navigationRequest.current;
    setLoadingNext(true);
    setNextError(false);
    setPreviousError(false);
    try {
      await queryClient.fetchQuery({
        ...tracePageOptions(principal, search, status, cursor),
        staleTime: 0,
      });
      if (request !== navigationRequest.current) return;
      setPageCursors((current) => [...current.slice(0, pageIndex + 1), cursor]);
      setPageIndex(pageIndex + 1);
      setExpanded(new Set());
      setRelatedEvent(null);
    } catch {
      if (request === navigationRequest.current) setNextError(true);
    } finally {
      if (request === navigationRequest.current) setLoadingNext(false);
    }
  };
  const previousPage = async () => {
    if (
      !active ||
      pageIndex === 0 ||
      loadingNext ||
      loadingPrevious ||
      query.isFetching
    )
      return;
    const request = ++navigationRequest.current;
    setLoadingPrevious(true);
    setPreviousError(false);
    setNextError(false);
    try {
      await queryClient.fetchQuery(
        tracePageOptions(principal, search, status, pageCursors[pageIndex - 1]),
      );
      if (request !== navigationRequest.current) return;
      setPageIndex(pageIndex - 1);
      setExpanded(new Set());
      setRelatedEvent(null);
    } catch {
      if (request === navigationRequest.current) setPreviousError(true);
    } finally {
      if (request === navigationRequest.current) setLoadingPrevious(false);
    }
  };
  const refresh = async () => {
    setRefreshing(true);
    setNextError(false);
    setPreviousError(false);
    queryClient.removeQueries({
      queryKey: ["event-traces", principal, "detail"],
      type: "inactive",
    });
    try {
      const [page] = await Promise.all([
        query.refetch(),
        queryClient.refetchQueries({
          queryKey: ["event-traces", principal, "detail"],
          type: "active",
        }),
      ]);
      if (page.isSuccess) {
        setPageCursors((current) => current.slice(0, pageIndex + 1));
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <section
      className="min-w-0 space-y-4 font-body text-ink-body"
      data-dd-privacy="hidden"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-ink">Event traces</h2>
          <p className="mt-1 text-sm text-annotation">
            Recorded decisions and their execution, newest first. Times shown in{" "}
            {timezone}.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => void refresh()}
            disabled={
              !active ||
              query.isFetching ||
              loadingNext ||
              loadingPrevious ||
              refreshing
            }
          >
            {refreshing ? "Refreshing" : "Refresh"}
          </Button>
        </div>
      </div>
      <div className="flex max-w-2xl flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1">
          <Input
            label="Search events"
            placeholder="Event ID, kind, or current turn text"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            label="Status"
            value={status}
            options={STATUS_OPTIONS}
            onValueChange={(value) => {
              if (value === status) return;
              resetPage();
              setStatus(value as TraceSummary["status"] | "");
            }}
          />
        </div>
      </div>
      {relatedEvent && !seen.has(relatedEvent) && (
        <aside className="rounded-paper border border-rule bg-card">
          <div className="flex items-center justify-between px-4 py-2">
            <p className="text-sm font-medium text-ink">Linked event</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRelatedEvent(null)}
            >
              Close
            </Button>
          </div>
          <TraceDetail
            eventUuid={relatedEvent}
            timezone={timezone}
            active={active}
            onOpenEvent={openRelated}
          />
        </aside>
      )}
      <div className="overflow-hidden rounded-paper border border-rule bg-card">
        <div className="hidden grid-cols-[auto_minmax(8rem,1fr)_minmax(10rem,2fr)_minmax(7rem,1fr)_auto] gap-4 border-b border-rule px-4 py-2 text-annotation-caps text-annotation sm:grid">
          <span />
          <span>Time</span>
          <span>Event</span>
          <span>Outcome · room</span>
          <span>Elapsed</span>
        </div>
        {query.isPending && <SkeletonStack count={5} className="p-5" />}
        {query.isError && !query.data && (
          <PanelError
            label="event traces"
            onRetry={() => void query.refetch()}
            className="min-h-48"
          />
        )}
        {query.isError && query.data && (
          <PanelError
            label="current page"
            onRetry={() => void refresh()}
            className="min-h-28"
          />
        )}
        {query.data && rows.length === 0 && (
          <EmptyNote>No recorded events match this view.</EmptyNote>
        )}
        {rows.length > 0 && (
          <ul>
            {rows.map((trace) => (
              <TraceRow
                key={trace.event_uuid}
                trace={trace}
                timezone={timezone}
                expanded={expanded.has(trace.event_uuid)}
                active={active}
                onToggle={() => toggle(trace)}
                onOpenEvent={openRelated}
              />
            ))}
          </ul>
        )}
      </div>
      {query.data && (
        <nav className="flex items-center gap-3" aria-label="Trace pages">
          <Button
            variant="outline"
            size="sm"
            disabled={
              !active ||
              pageIndex === 0 ||
              query.isFetching ||
              loadingNext ||
              loadingPrevious ||
              refreshing
            }
            busy={loadingPrevious}
            onClick={() => void previousPage()}
          >
            Previous
          </Button>
          <span className="text-sm text-ink-body">Page {pageIndex + 1}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={
              !active ||
              !query.data.next_cursor ||
              query.isFetching ||
              loadingNext ||
              loadingPrevious ||
              refreshing
            }
            busy={loadingNext}
            onClick={() => void nextPage()}
          >
            Next
          </Button>
        </nav>
      )}
      {nextError && (
        <PanelError
          label="next page"
          onRetry={() => void nextPage()}
          className="min-h-28"
        />
      )}
      {previousError && (
        <PanelError
          label="previous page"
          onRetry={() => void previousPage()}
          className="min-h-28"
        />
      )}
      {query.data && rows.length > 0 && (
        <p className="text-xs text-annotation">
          Showing {rows.length} event{rows.length === 1 ? "" : "s"} on this
          page.
        </p>
      )}
    </section>
  );
};
